import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CloudWatchLogsClient,
  CloudWatchLogsClientConfig,
  DescribeLogStreamsCommand,
  InputLogEvent,
  PutLogEventsCommand,
  PutLogEventsRequest
} from '@aws-sdk/client-cloudwatch-logs';
import { environment } from '@root/environments/environment';
import { AdminService } from '../admin/admin.service';
import { RequestResponse } from '../rfq/rfq.types';

const CW = environment.cloudWatch;

const { logGroupName: LOG_GROUP_NAME, logStreamName: LOG_STREAM_NAME } =
  CW.logNames;

const CW_CONFIG: CloudWatchLogsClientConfig = {
  region: CW.region,
  credentials: {
    accessKeyId: CW.accessKeyId,
    secretAccessKey: CW.secretAccessKey
  }
};

const MAX_RETRY_ATTEMPTS = 3;

@Injectable({
  providedIn: 'root'
})
export class CloudwatchLogService {
  private cloudwatchlogs = new CloudWatchLogsClient(CW_CONFIG);
  private logEvents: InputLogEvent[] = [];

  constructor(private adminService: AdminService) {
    console.log('CloudwatchLogService init');
    setInterval(() => this.sendLogs(), 3000);
  }

  private sendLogs(retryAttempts = 1) {
    if (this.logEvents.length === 0) {
      return;
    }

    const params = { logGroupName: LOG_GROUP_NAME };

    const command = new DescribeLogStreamsCommand(params);

    this.cloudwatchlogs.send(command, async (_, data) => {
      const numEvents = this.logEvents.length;
      console.log('Sending logs', numEvents, retryAttempts);

      const handleError = () => {
        if (retryAttempts < MAX_RETRY_ATTEMPTS) {
          setTimeout(() => this.sendLogs(retryAttempts + 1), 500);
        } else {
          this.logEvents = this.logEvents.slice(numEvents);
        }
      };

      const stream = data.logStreams.find(
        s => s.logStreamName === LOG_STREAM_NAME
      );
      const sequenceToken = stream?.uploadSequenceToken;
      if (!sequenceToken) {
        console.error('No sequence token found for', LOG_STREAM_NAME);
        handleError();
        return;
      }

      try {
        await this.uploadBatch(sequenceToken, [...this.logEvents]);
        // The array could have been appended to in the meantine, so ensure we
        // only clear the events sent.
        this.logEvents = this.logEvents.slice(numEvents);
      } catch (error) {
        handleError();
      }
    });
  }

  public logError(
    errorToLog: RequestResponse | HttpErrorResponse,
    errorMessage?: string
  ) {
    if (errorToLog instanceof HttpErrorResponse) {
      this.addLogEntry({
        user: this.adminService.userInfo.email,
        response: {
          ...errorToLog,
          headers: errorToLog.headers
            ? this.extractHeaders(errorToLog.headers)
            : []
        },
        errorMessage
      });
    } else {
      const errorResponse = errorToLog?.response;
      let request = errorToLog?.requestData?.request;
      let reqObj = undefined;
      if (request) {
        if (request.body?.password) {
          request.body.password = '**************';
        }
        reqObj = {
          ...request,
          headers: this.extractHeaders(request.headers),
          params: undefined,
          timestamp: errorToLog.requestData.timestamp
        };
      }

      this.addLogEntry({
        user: this.adminService.userInfo.email,
        request: reqObj,
        response: {
          ...errorResponse,
          headers: errorResponse
            ? this.extractHeaders(errorResponse.headers)
            : []
        },
        errorMessage
      });
    }
  }

  private addLogEntry(message: any) {
    const timestamp = new Date().getTime();
    this.logEvents.push({ message: JSON.stringify(message), timestamp });
  }

  private async uploadBatch(
    sequenceToken: string,
    logEvents: InputLogEvent[]
  ): Promise<any> {
    const params: PutLogEventsRequest = {
      logGroupName: LOG_GROUP_NAME,
      logStreamName: LOG_STREAM_NAME,
      sequenceToken,
      logEvents
    };

    const command = new PutLogEventsCommand(params);

    return this.cloudwatchlogs.send(command);
  }

  private extractHeaders(headers: HttpHeaders) {
    return headers.keys().map(key => {
      return { key, value: headers.get(key) };
    });
  }
}
